import {createSlice, createAsyncThunk, current} from "@reduxjs/toolkit";
import { api, apiErrorsHandler } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { notificationDurationShort, notificationDurationMiddle, notificationDurationLong } from "../../lib/utils";
import i18next from "i18next";
 

const CLEANUPS_STRATEGIES_COUNT = 5;


export const operatorsLookup = [
    {id: 0, title: i18next.t("slices.none")}, {id: 1, title: "="}, {id: 2, title: "<>"}, 
    {id: 3, title: "<"}, {id: 4, title: "<="}, {id: 5, title: ">"}, {id: 6, title: ">="}
  ];


const defaultFormInitialValues = {
    RetentionPeriodInDays: 365,
    Cleanups: []
};


const sizeDisplayRenderer = (columnName) => (schemaItem, dataItem) => {
  let value = dataItem[columnName];

  if (!value || isNaN(parseFloat(value))) return value;

  // Keep precision to 2 decimals
  const format = (val, unit) => `${Math.round(val * 100) / 100} ${unit}`;

  if (value < 1024) {
    return format(value, "MB");
  } else if (value < 1024 * 1024) {
    return format(value / 1024, "GB");
  } else {
    return format(value / (1024 * 1024), "TB");
  }
};


function createDatasetsTableSchema(dispatch, currentUser)
{
    const canManageDataConsumption = currentUser && currentUser.Extras && currentUser.Extras.ManageConsumption;

    return [
        {id: "DatasetTitle", dataType: 3, title:  "slices.dataset"},
        {id: "RowsCount", dataType: 2, title:  "slices.rows", thousandSeparator: ","},
        {id: "TotalSizeInMB", dataType: 2, title:  "slices.space", displayRenderer: sizeDisplayRenderer("TotalSizeInMB"), decimalPlaces: 2},
        {id: "Percentage", dataType: 2, title:  "slices.ratio", displayRenderer: (schemaItem, dataItem) => Math.round(dataItem["Percentage"]*100)/100 + "%", decimalPlaces: 2},
        {id: "ImportedRowsCount", dataType: 2, title:  "slices.importedRows", thousandSeparator: ","},
        {id: "ImportedSizeInMB", dataType: 2, title:  "slices.importedSpace", displayRenderer: sizeDisplayRenderer("ImportedSizeInMB"), decimalPlaces: 2},
        {id: "CleanedRowsCount", dataType: 2, title:  "slices.cleanedRows", thousandSeparator: ","},
        {id: "CleanedSizeInMB", dataType: 2, title:  "slices.cleanedSpace",displayRenderer: sizeDisplayRenderer("CleanedSizeInMB"), decimalPlaces: 2 },        
        {id: "Details", dataType: 3, title:  "slices.details", onCellLinkClick: (schemaItem, dataItem) => {
            const datasetTitle = dataItem.DatasetTitle;
            dispatch(showDatasetByMonthDialogOuter({datasetTitle: datasetTitle}));
        }},
        {id: "RetentionPeriodInDays", dataType: 2, title:  "slices.retentionPeriod", 
              displayRenderer: (schemaItem, dataItem) => `${dataItem.RetentionPeriodInDays} days`,
              onCellLinkClick: canManageDataConsumption ? (schemaItem, dataItem) => {
                const datasetTitle = dataItem.DatasetTitle;
                dispatch(showSetRetentionPeriodDialogOuter({datasetTitle: datasetTitle}));
            } : undefined
        },
        {id: "Cleanups", dataType: 2, title:  "slices.cleanups", onCellLinkClick: canManageDataConsumption ? (schemaItem, dataItem) => {
            const datasetTitle = dataItem.DatasetTitle;
            dispatch(showCleanupsDialogOuter({datasetTitle: datasetTitle}));
            } : undefined
        }
    ];
}


function getEmptyCleanupStrategies()
{
    let result = [];

    for (let i = 0; i< CLEANUPS_STRATEGIES_COUNT; i++)
        result.push({FieldId: 0, FieldName: "None", OperatorId: 0, OperatorName: "None", Value: 0, RetentionPeriodInDays: 0});        
    
    return result;
}


function getCleanupStrategies(detail)
{
    let result = [];
    let existing = detail.Cleanups?.length;
    if (existing && existing > 0)
    {
        for (let c of detail.Cleanups)        
            result.push({FieldId: c.FieldId, FieldName: c.FieldName, OperatorId: c.OperatorId, OperatorName: c.OperatorName, Value: c.Value, RetentionPeriodInDays: c.RetentionPeriodInDays});        
    }

    if (existing < CLEANUPS_STRATEGIES_COUNT)
    {
        for (let i = existing; i< CLEANUPS_STRATEGIES_COUNT; i++)
            result.push({FieldId: 0, FieldName: "None", OperatorId: 0, OperatorName: "None", Value: 0, RetentionPeriodInDays: 0});        
    }

    return result;
}


function setCleanupStrategies(strategies)
{
    let result = [];

    if (!strategies || strategies.length == 0)
        return result;

    for (let s of strategies)
    {
        if (s.FieldId > 0 && s.OperatorId > 0)        
            result.push({...s, value: parseFloat(s.Value), RetentionPeriodInDays: parseInt(s.RetentionPeriodInDays)});        
    }

    return result;
}


const initialState = {
    pageLoaded: false, pageDisposed: true,
    processing: false, processingText: "",
    mode: 0, 
    
    formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formServerErrors: [], 
    databaseSubscriptionMaximumSizeInMB: 0, databaseTotalSizeInMB: 0, databaseUsedSizeInMB: 0, databaseUnusedSizeInMB: 0, databaseRemainingSizeInMB:0,  
    databaseUsedPercentage: 0, databaseImportedSizeInMB: 0, databaseCleanedSizeInMB: 0,    
    databaseByMonth: [],
    datasets:[],
    details: [], databaseByMonthTable: [], datasetsByMonthTable: [],
    setRetentionPeriodDialogOpen: false, databaseByMonthDialogOpen: false, datasetByMonthDialogOpen: false, cleanupsDialogOpen: false,
    selectedDatasetId: null,
    canManageDataConsumption: false,
    panes: [                    
        {
            // size: "30%",
            size:"20%",
            min: "18%",
            max: "25%",
            collapsible: false
        },           
        {
            //min: "700px",
            //size:"60%",
            min: "53%",
            max: "66%",
            collapsible: false,
        },
        {
            //min: "700px",
            size:"18%",
            min: "16%",
            max: "22%",
            collapsible: false,
        }
      ]    
};

const dataConsumptionPageSlice = createSlice({
    name: "dataConsumptionPage",
    initialState: initialState,
    reducers:{
        setInitialState: (state, action) =>  ({...initialState}),//no payload

        setProcessing: (state, action) => {
            //payload: {processing=true, processingText=false}, when processing is false, processingText is "" 
            const {processing=true, processingText=""} = action.payload;

            return ({...state, processing: processing, processingText: processing==false ? "" : processingText});
        },

        setPanes: (state, action) => ({...state, panes: {...action.payload}}),

        setPageValue: (state, action) => ({...state, [action.payload.id]: action.payload.value}),//payload: {id: , value:}

        setFormValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                formValues: {...state.formValues, [id]: value}
            });
        },

        setFieldValue: (state, action) => {
            //payload is the field - sending a field => replace the field with that id with the passed field's properties
            const pageState = current(state);
            const entity = action.payload;

            return ({...state, formValues: {...state.formValues, Fields: state.formValues.Fields.map(item =>{
                if (item.Id == entity.Id)
                   return {...entity};
                else 
                return item;
            })}});
        },

        setFormServerErrors: (state, action) => ({...state, formServerErrors: [...action.payload]}),

        pageLoaded : (state, action) => {
            //payload: {  }
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;

            let payload = action.payload;

            //compute datasetsTable
            let datasetsTable = {
                schema: createDatasetsTableSchema(payload.dispatch, payload.currentUser),
                data: payload.Details.map(e => ({
                    DatasetTitle: e.DatasetTitle, RowsCount: e.RowsCount, TotalSizeInMB: e.TotalSizeInMB, Percentage: e.Percentage,
                    ImportedRowsCount: e.ImportedRowsCount, ImportedSizeInMB: e.ImportedSizeInMB, 
                    CleanedRowsCount: e.CleanedRowsCount, CleanedSizeInMB: e.CleanedSizeInMB, 
                    RetentionPeriodInDays: e.RetentionPeriodInDays,
                    Cleanups: e.Cleanups && e.Cleanups.length > 0 ? e.Cleanups.length : 0,
                    Details: "By Month"
                 }))                
            };

            let databaseByMonthTable = {
                schema: [
                    {id: "Year", dataType: 2, title: i18next.t("slices.year")},
                    {id: "Month", dataType: 2, title: i18next.t("slices.month")},
                    {id: "ImportedRowsCount", dataType: 2, title: i18next.t("slices.importedRows"), thousandSeparator: ","},
                    {id: "ImportedSizeInMB", dataType: 2, title: i18next.t("slices.importedSpace"), displayRenderer: sizeDisplayRenderer("ImportedSizeInMB"), decimalPlaces: 2},
                    {id: "CleanedRowsCount", dataType: 2, title: i18next.t("slices.cleanedRows"), thousandSeparator: ","},
                    {id: "CleanedSizeInMB", dataType: 2, title: i18next.t("slices.cleanedSpace"), displayRenderer: sizeDisplayRenderer("CleanedSizeInMB"), decimalPlaces: 2},
                ],
                data: payload.DatabaseByMonth.map(e => ({
                    Year: e.Year,
                    Month: e.Month,
                    ImportedRowsCount: e.ImportedRowsCount,
                    ImportedSizeInMB: e.ImportedSizeInMB,
                    CleanedRowsCount: e.CleanedRowsCount,
                    CleanedSizeInMB: e.CleanedSizeInMB
                })).sort((a, b) => b.Year - a.Year || b.Month - a.Month)
            };

            return {...state, 
                pageLoaded: true,
                processing: false, processingText: "",
                mode: 0, 
                formKey: (new Date()).toString(), formValues:{...defaultFormInitialValues}, formInitialValues:{...defaultFormInitialValues}, formServerErrors:[],            
                databaseSubscriptionMaximumSizeInMB: payload.DatabaseSubscriptionMaximumSizeInMB, 
                databaseTotalSizeInMB: payload.DatabaseTotalSizeInMB, databaseUsedSizeInMB: payload.DatabaseUsedSizeInMB, databaseUnusedSizeInMB: payload.DatabaseUnusedSizeInMB, 
                databaseRemainingSizeInMB: payload.DatabaseRemainingSizeInMB, databaseUsedPercentage: payload.DatabaseUsedPercentage,
                databaseImportedSizeInMB: payload.DatabaseImportedSizeInMB, databaseCleanedSizeInMB: payload.DatabaseCleanedSizeInMB,    
                databaseByMonth: payload.DatabaseByMonth,
                datasets:payload.Datasets,
                details: payload.Details,
                databaseByMonthTable: databaseByMonthTable,
                datasetsTable: datasetsTable,
                canManageDataConsumption: payload.currentUser && payload.currentUser.Extras && payload.currentUser.Extras.ManageConsumption
            };
        },

        showSetRetentionPeriodDialog: (state, action) => {
            const pageState = current(state);
            let payload = action.payload;
            let datasetTitle = payload.datasetTitle;
            let datasetId = pageState.datasets.find(e => e.Name == datasetTitle).Id;//.Name contains the dataset title       
            let currentRetentionPeriodInDays = pageState.details.find(e => e.DatasetId == datasetId).RetentionPeriodInDays;

            return {
                ...state,
                setRetentionPeriodDialogOpen: true, databaseByMonthDialogOpen: false, datasetByMonthDialogOpen: false, 
                cleanupsDialogOpen: false, selectedDatasetId: datasetId,
                formKey: (new Date()).toString(), 
                formValues:{...defaultFormInitialValues, RetentionPeriodInDays: currentRetentionPeriodInDays}, 
                formInitialValues:{...defaultFormInitialValues, RetentionPeriodInDays: currentRetentionPeriodInDays}, 
                formServerErrors:[],            
            };
        },

        setRetentionPeriod: (state, action) => {
            const pageState = current(state);
            const datasetId = pageState.selectedDatasetId;
            const datasetTitle = pageState.datasets.find(e => e.Id == datasetId).Name;            
            const retentionPeriodInDays = pageState.formValues.RetentionPeriodInDays;

            return {...state, 
                setRetentionPeriodDialogOpen: false, selectedDatasetId: null,
                details: state.details.map(e => {
                    if (e.DatasetId != datasetId)
                        return e;
                    return {...e, RetentionPeriodInDays: parseInt(retentionPeriodInDays)};
                }),
                datasetsTable: {
                    ...state.datasetsTable,
                    data: state.datasetsTable.data.map(e => {
                        if (e.DatasetTitle != datasetTitle)
                            return e;
                        return {...e, RetentionPeriodInDays: parseInt(retentionPeriodInDays)}
                    })
                }
            }
        },

        showDatabaseByMonthDialog: (state, action) => {
            return {
                ...state,
                setRetentionPeriodDialogOpen: false, databaseByMonthDialogOpen: true, datasetByMonthDialogOpen: false, 
                cleanupsDialogOpen: false,selectedDatasetId: null,
            };
        },

        showDatasetByMonthDialog: (state, action) => {
            const pageState = current(state);
            let payload = action.payload;
            let datasetTitle = payload.datasetTitle;
            let datasetId = payload.datasetId;            

            let datasetByMonthTable = {
                schema: [
                    {id: "Year", dataType: 2, title: i18next.t("slices.year")},
                    {id: "Month", dataType: 2, title: i18next.t("slices.month")},
                    {id: "ImportedRowsCount", dataType: 2, title: i18next.t("slices.importedRows"), thousandSeparator: ","},
                    {id: "ImportedSizeInMB", dataType: 2, title: i18next.t("slices.importedSpace"), displayRenderer: sizeDisplayRenderer("ImportedSizeInMB"), decimalPlaces: 2},
                    {id: "CleanedRowsCount", dataType: 2, title: i18next.t("slices.cleanedRows"), thousandSeparator: ","},
                    {id: "CleanedSizeInMB", dataType: 2, title: i18next.t("slices.cleanedSpace"), displayRenderer: sizeDisplayRenderer("CleanedSizeInMB"), decimalPlaces: 2},
                ],
                data: pageState.details.find(e => e.DatasetId == datasetId).ByMonth.map(e => ({
                    Year: e.Year,
                    Month: e.Month,
                    ImportedRowsCount: e.ImportedRowsCount,
                    ImportedSizeInMB: e.ImportedSizeInMB,
                    CleanedRowsCount: e.CleanedRowsCount,
                    CleanedSizeInMB: e.CleanedSizeInMB
                }))
            };

            return {
                ...state,
                setRetentionPeriodDialogOpen: false, databaseByMonthDialogOpen: false, datasetByMonthDialogOpen: true, 
                cleanupsDialogOpen: false, selectedDatasetId: datasetId,
                datasetByMonthTable: datasetByMonthTable
            };
        },

        showCleanupsDialog: (state, action) => {
            const pageState = current(state);
            let payload = action.payload;
            let datasetTitle = payload.datasetTitle;
            let datasetId = payload.datasetId;            

            let detail = pageState.details.find(e => e.DatasetId == datasetId);
            let strategies = getCleanupStrategies(detail);
            let fieldsLookup = [{id: 0, title: "None"}, ...pageState.datasets.find(e => e.Id == datasetId).Fields.map(e => ({id: e.Id, title: e.Title}))];
            

            return {
                ...state,
                setRetentionPeriodDialogOpen: false, databaseByMonthDialogOpen: false, datasetByMonthDialogOpen: false, 
                cleanupsDialogOpen: true,selectedDatasetId: datasetId,
                formKey: (new Date()).toString(), 
                formValues:{...defaultFormInitialValues, Cleanups: [...strategies], FieldsLookup: fieldsLookup, OperatorsLookup:  operatorsLookup}, 
                formInitialValues:{...defaultFormInitialValues, Cleanups: [...strategies], FieldsLookup: fieldsLookup, OperatorsLookup:  operatorsLookup}, 
                formServerErrors:[],     
            };
        },

        clearAllCleanups: (state, action) => {
            const pageState = current(state);
            const datasetId = pageState.selectedDatasetId;

            let arr = getEmptyCleanupStrategies();

            return {...state, 
                formKey: (new Date()).toString(), 
                formInitialValues: {...state.formInitialValues, Cleanups: [...arr]},
                formValues: {...state.formValues, Cleanups: [...arr]}
            }
        },

        clearCleanupStrategy: (state, action) => {
            const pageState = current(state);
            const index = action.payload;

            let arr = pageState.formValues.Cleanups.map((e,i) => {
                if (i != index)
                    return e;
                return ({FieldId: 0, FieldName: "None", OperatorId: 0, OperatorName: "None", Value: 0, RetentionPeriodInDays: 0});
            });


            return {...state, 
                formKey: (new Date()).toString(), 
                formInitialValues: {...state.formInitialValues,
                    Cleanups: [...arr]
                },
                formValues: {...state.formValues,
                    Cleanups: [...arr]
                }
            }
        },

        setCleanups: (state, action) => {
            const pageState = current(state);
            const datasetId = pageState.selectedDatasetId;
            const datasetTitle = pageState.datasets.find(e => e.Id == datasetId).Name;            

            const newCleanups = setCleanupStrategies(pageState.formValues.Cleanups);

            return {...state, 
                cleanupsDialogOpen: false, selectedDatasetId: null,
                details: state.details.map(e => {
                    if (e.DatasetId != datasetId)
                        return e;
                    return {...e, Cleanups: [...newCleanups]};
                }),
                datasetsTable: {
                    ...state.datasetsTable,
                    data: state.datasetsTable.data.map(e => {
                        if (e.DatasetTitle != datasetTitle)
                            return e;
                        return {...e, Cleanups: newCleanups.length}
                    })
                }
            }
        },

        disposePage: (state, action) => {
            //payload: bool  
            if (action.payload)
                return {...initialState};
            else  
                return {...state, pageDisposed: false};
        },
    }
});

export default dataConsumptionPageSlice.reducer;


export const loadPage = (t) => async (dispatch, getState) => {    
    //start with initial values
    dispatch(setInitialState());

    //undispose page
    dispatch(disposePage(false));

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingPage")}));

    try{
        var response = await api({            
                url: 'dataconsumption/getdataconsumption',
                method:  'post',
                data: {
                },
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().dataConsumptionPage.pageDisposed)
           return;
           
        //it's ok to dispatch here
        let data = response.data.Data;

        //transform the response
       
        dispatch(pageLoaded({...data, currentUser: getState().currentUser, dispatch: dispatch, t:t}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export function showSetRetentionPeriodDialogOuter(obj){
    return  async (dispatch, getState) => {
        dispatch(showSetRetentionPeriodDialog({datasetTitle: obj.datasetTitle}));
    }
};


export function showDatabaseByMonthDialogOuter(){
    return  async (dispatch, getState) => {
        dispatch(showDatabaseByMonthDialog());
    }
};


export function showDatasetByMonthDialogOuter(obj){
    return  async (dispatch, getState) => {
        let datasetId = getState().dataConsumptionPage.datasets.find(e => e.Name == obj.datasetTitle).Id;

        dispatch(showDatasetByMonthDialog({datasetId: datasetId, datasetTitle: obj.datasetTitle}));
    }
};


export function showCleanupsDialogOuter(obj){
    return  async (dispatch, getState) => {
        let datasetId = getState().dataConsumptionPage.datasets.find(e => e.Name == obj.datasetTitle).Id;

        dispatch(showCleanupsDialog({datasetId: datasetId, datasetTitle: obj.datasetTitle}));
    }
};


export const saveEntity = (t) => async (dispatch, getState) => {
    const pageState = getState().dataConsumptionPage;
    let url = "dataconsumption/savedataconsumption";
    let title = t("slices.savingData");

     //show loader
     dispatch(setProcessing({processing: true, processingText: title}));
     
    let details = pageState.details.map(e => ({
        DatasetId: e.DatasetId,
        RetentionPeriodInDays: e.RetentionPeriodInDays,
        Cleanups: !e.Cleanups || e.Cleanups.length == 0 ? [] :
                   e.Cleanups.map(c => ({
                    FieldId: c.FieldId, 
                    FieldName: c.FieldName, 
                    OperatorId: c.OperatorId, 
                    OperatorName: c.OperatorName, 
                    Value: c.Value, 
                    RetentionPeriodInDays: c.RetentionPeriodInDays
                   }))
    }))

    
    try{
        var response = await api({            
            url:url,
            method:  'post',
            data: {
                details: details
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().dataConsumptionPage.pageDisposed)
           return;

        let data = response.data.Data;
        
        let successMessage = t("slices.savedData");

        dispatch(addNotification({text: successMessage, type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};


export const { setInitialState, setProcessing, setPageValue, pageLoaded, setPanes, setFormValue, setFormServerErrors, showSetRetentionPeriodDialog, setRetentionPeriod, showDatabaseByMonthDialog, showDatasetByMonthDialog, showCleanupsDialog, clearAllCleanups, clearCleanupStrategy, setCleanups, disposePage } = dataConsumptionPageSlice.actions;

